import { useFormContext } from "react-hook-form";
import { config } from "../../../../../config";
import { useSupabaseCallback } from "../../../../../server/supabase/hooks";
import { toISO8601 } from "../../../../../utils/iso8601";
import { uuidv4 } from "../../../../../utils/uuid";
import { useAnalytics } from "../../../../analytics/analytics";
import { SupabaseCustomEmailService } from "../../../../channels/email/server/supabase-custom-email-service";
import { SupabaseSmsConfigService } from "../../../../channels/sms/server/supabase-sms-config-service";
import {
  isChatChannel,
  isEmailChannel,
  isTextChannel,
  type ChannelName,
} from "../../../../channels/types";
import { EmailMomentLoader } from "../../../../email-moment/server/email-moment-loader";
import type { EmailMoment } from "../../../../email-moment/types";
import { makeDefaultLexicalDesign } from "../../../../lexical-chat-moment/make-default-lexical-design";
import type { Tags } from "../../../../merge-tag/types";
import type {
  SerialisedEditorDesign,
  SerialisedLexicalEditorDesign,
} from "../../../../message/editor/types/serialised-editor-design";
import { useChatChannelRecipients } from "../../../../message/editor/utils/use-chat-channel-recipients";
import {
  SupabaseSlackAliasDefaultService,
  SupabaseSlackAppService,
} from "../../../../slack/server/slack-supabase-service";
import { designContainsSurveyAnswerBlock } from "../../../../unlayer-email-content/hooks/use-unlayer-editor";
import type { MomentFormMoment } from "../../../types/moment-form-moment";
import { convertLexicalDesign, designContainsSurveyAnswers } from "../utils/convert-lexical-design";

export function useHandleReset(
  newsletter?: boolean,
): (newChannel?: ChannelName, mergeTags?: Tags) => Promise<void> {
  const { reset, getValues } = useFormContext<MomentFormMoment>();
  const { data: slackChannelRecipients } = useChatChannelRecipients("slack");
  const { gaEvent } = useAnalytics();
  return useSupabaseCallback(
    async ({ supabase, account_id }, newChannel?: ChannelName, mergeTags?: Tags) => {
      // Initialize services
      const slackAliasService = new SupabaseSlackAliasDefaultService(supabase);
      const customEmailService = new SupabaseCustomEmailService(supabase);
      const emailMomentLoader = new EmailMomentLoader(supabase);
      const smsConfigService = new SupabaseSmsConfigService(supabase);
      const slackAppService = new SupabaseSlackAppService(supabase);
      // Get sender information based on channel type
      const getSenderInfo = async (): Promise<string | null> => {
        if (!newChannel) return null;

        if (newChannel === "slack") {
          const { data } = await slackAliasService.getSingle();
          return data?.slack_alias_id || null;
        }

        if (!newsletter && isEmailChannel(newChannel)) {
          const { data } = await customEmailService.getDefault();
          return data?.address || config.defaultEmailFrom;
        }

        if (isChatChannel(newChannel)) {
          const { data } = await slackAliasService.getSingle();
          return data?.slack_alias_id || null;
        }

        return null;
      };

      // Get email moment if needed
      const getEmailMoment = async (): Promise<EmailMoment | null> => {
        if (newChannel && isEmailChannel(newChannel) && newsletter) {
          return emailMomentLoader.loadNew();
        }
        return null;
      };

      const slackVideoEnabled = await slackAppService.videoEnabled(account_id);
      const currentDesign = getValues("design");

      if (
        isTextChannel(newChannel) &&
        (designContainsSurveyAnswers(currentDesign as SerialisedLexicalEditorDesign | null) ||
          designContainsSurveyAnswerBlock(getValues("email_moment.design")))
      ) {
        gaEvent("survey_response_block_removed", { action: "channel_unsupported" });
      }

      let design;
      if (currentDesign) {
        design = convertLexicalDesign(
          getValues("channel") ?? "work_email",
          newChannel ?? "work_email",
          currentDesign as SerialisedLexicalEditorDesign,
          slackVideoEnabled,
          slackChannelRecipients || [],
        );
      } else {
        if (newChannel === "twilio") {
          const smsConfig = await smsConfigService.maybeGet();
          const companyName = smsConfig.data?.prepend_company_name
            ? mergeTags?.filter((tag) => tag.version === 2).find((x) => x.id === "company_name")
                ?.fallback_value
            : null;
          design = makeDefaultLexicalDesign("sms", smsConfig?.data?.footer, companyName ?? null);
        } else {
          design = {} as SerialisedEditorDesign;
        }
      }

      // Build new form values
      const newValues = {
        channel: newChannel ?? null,
        email_moment: await getEmailMoment(),
        chat_moment: null,
        additional_recipients: [],
        from: await getSenderInfo(),
        use_lexical_editor: !newsletter,
        design,
        email_subject: null,
        body: null,
        whatsapp_template_id: newChannel === "whatsapp" ? getValues("whatsapp_template_id") : null,
        whatsapp_moment_id: newChannel === "whatsapp" ? getValues("whatsapp_moment_id") : null,
        whatsapp_moment:
          newChannel === "whatsapp"
            ? (getValues("whatsapp_moment") ?? {
                id: uuidv4(),
                account_id,
                created_at: toISO8601(new Date()),
                destination_type: "page",
                digital_asset_id: null,
                url: null,
              })
            : null,
      };

      reset((values) => ({ ...values, ...newValues }), { keepDirty: true });
    },
    [reset, newsletter, getValues, slackChannelRecipients, gaEvent],
  );
}
